// src/components/ScreenerResult.tsx
import React, { useRef, useState } from "react";
import type { AnalyzeScreenerResponse } from "../api/myTypes";
import { trackRecommendation, trackPosition } from "../api/etfApi";

interface ScreenerResultProps {
  data: AnalyzeScreenerResponse;
}

const ScreenerResult: React.FC<ScreenerResultProps> = ({ data }) => {
  const screenerOutput = data.screener_output || {};
  const validationOutput = data.validation_output || {};
  const marketRegime = data.market_regime;
  const isFirstCandleRule = data.screener === "first_candle_rule";
  const isMacdMoneyMap = data.screener === "macd_money_map";
  const isUserSpecified = data.screener === "user_specified";
  
  const [showFullScreenerOutput, setShowFullScreenerOutput] = useState(true);
  const [showFullValidationOutput, setShowFullValidationOutput] = useState(true);
  const [trackingStatus, setTrackingStatus] = useState<{ type: "recommendation" | "position" | null; message: string }>({ type: null, message: "" });
  
  const validationPrintRef = useRef<HTMLDivElement>(null);

  // Get regime badge color
  const getRegimeBadgeColor = (regimeType: string) => {
    if (regimeType?.includes("Bull")) return "#10b981"; // green
    if (regimeType?.includes("Bear")) return "#ef4444"; // red
    if (regimeType?.includes("Choppy") || regimeType?.includes("Sideways")) return "#f59e0b"; // amber
    if (regimeType?.includes("High Volatility")) return "#8b5cf6"; // purple
    if (regimeType?.includes("Low Volatility")) return "#3b82f6"; // blue
    return "#6b7280"; // gray
  };

  const handleTrackRecommendation = async () => {
    try {
      const recommendationId = `rec_${data.ticker}_${data.screener}_${Date.now()}`;
      
      // Extract entry price from screener output if available
      const entryPrice = screenerOutput.entry_price || screenerOutput.entry_price_zone?.match(/\$?([\d.]+)/)?.[1];
      const entryPriceNum = entryPrice ? parseFloat(entryPrice) : undefined;
      
      await trackRecommendation({
        recommendationId,
        ticker: data.ticker,
        strategy: data.screener,
        model: "screener",
        entryPrice: entryPriceNum,
        recommendationData: screenerOutput,
        marketRegime: marketRegime || undefined,
      });
      setTrackingStatus({ type: "recommendation", message: "✓ Recommendation tracked successfully!" });
      setTimeout(() => setTrackingStatus({ type: null, message: "" }), 5000);
      // Dispatch event to refresh Performance tab
      window.dispatchEvent(new CustomEvent('refresh-performance'));
    } catch (error: any) {
      setTrackingStatus({ type: "recommendation", message: `Error: ${error.message}` });
      setTimeout(() => setTrackingStatus({ type: null, message: "" }), 5000);
    }
  };

  // Helper function to extract price from various formats
  // Returns the average if it's a range, otherwise the single value
  const extractPrice = (priceStr: string | number | null | undefined, useAverage: boolean = true): number | undefined => {
    if (typeof priceStr === 'number') {
      return isNaN(priceStr) ? undefined : priceStr;
    }
    if (!priceStr) return undefined;
    
    const priceString = String(priceStr);
    
    // Try to extract number from various formats:
    // "$100", "100", "$100-105", "100-105", "$100.50", "100.50", "around $100", etc.
    
    // First, try to match a range pattern
    const rangePattern = /\$?\s*([\d,]+\.?\d*)\s*-\s*\$?\s*([\d,]+\.?\d*)/;
    const rangeMatch = priceString.match(rangePattern);
    
    if (rangeMatch && rangeMatch[1] && rangeMatch[2]) {
      // It's a range - calculate average if useAverage is true, otherwise use lower value
      const lower = parseFloat(rangeMatch[1].replace(/,/g, ''));
      const upper = parseFloat(rangeMatch[2].replace(/,/g, ''));
      
      if (!isNaN(lower) && !isNaN(upper) && lower > 0 && upper > 0 && upper >= lower) {
        return useAverage ? (lower + upper) / 2 : lower;
      }
    }
    
    // If not a range, try single number pattern
    const singlePattern = /\$?\s*([\d,]+\.?\d*)/;
    const singleMatch = priceString.match(singlePattern);
    
    if (singleMatch && singleMatch[1]) {
      const priceValue = singleMatch[1].replace(/,/g, '');
      const parsed = parseFloat(priceValue);
      if (!isNaN(parsed) && parsed > 0) {
        return parsed;
      }
    }
    
    return undefined;
  };

  const handleTrackPosition = async () => {
    try {
      const positionId = `pos_${data.ticker}_${Date.now()}`;
      const recommendationId = `rec_${data.ticker}_${data.screener}_${Date.now()}`;
      
      // Try multiple sources for entry price (use average for ranges)
      // PRIORITY: Extract from recommendation entry zones, NOT current price
      let entryPriceNum: number | undefined = undefined;
      let stopLossNum: number | undefined = undefined;
      let takeProfitNum: number | undefined = undefined;
      const trade = screenerOutput.trade as { entry?: number; stop?: number; targets?: number[] } | undefined;

      // 0. Validated trade object (Trend Reversal and other ScreenerDecision strategies)
      if (trade) {
        if (trade.entry != null && typeof trade.entry === "number" && !isNaN(trade.entry)) {
          entryPriceNum = trade.entry;
        }
        if (trade.stop != null && typeof trade.stop === "number" && !isNaN(trade.stop)) {
          stopLossNum = trade.stop;
        }
        if (Array.isArray(trade.targets) && trade.targets.length > 0) {
          const nums = trade.targets.filter((t): t is number => typeof t === "number" && !isNaN(t));
          if (nums.length > 0) {
            takeProfitNum = Math.max(...nums); // use highest target as take profit
          }
        }
      }

      // 1. For MACD Money Map: Check final_recommendations array first
      if (isMacdMoneyMap && screenerOutput.final_recommendations && Array.isArray(screenerOutput.final_recommendations) && screenerOutput.final_recommendations.length > 0) {
        const firstRec = screenerOutput.final_recommendations[0];
        if (firstRec.entry_price_zone && entryPriceNum == null) {
          entryPriceNum = extractPrice(firstRec.entry_price_zone, true); // Use average for ranges
        }
        if (firstRec.stop_loss && stopLossNum == null) {
          stopLossNum = extractPrice(firstRec.stop_loss, true);
        }
        if (firstRec.take_profit && takeProfitNum == null) {
          takeProfitNum = extractPrice(firstRec.take_profit, true);
        }
      }

      // 2. Try top-level entry_price_zone field (for other strategies)
      if (!entryPriceNum && screenerOutput.entry_price_zone) {
        entryPriceNum = extractPrice(screenerOutput.entry_price_zone, true); // true = use average for ranges
      }

      // 3. Try direct entry_price field (if it's a number)
      if (!entryPriceNum && screenerOutput.entry_price !== undefined) {
        entryPriceNum = extractPrice(screenerOutput.entry_price, true);
      }

      // 4. Try extracting from analysis_summary or other text fields (before falling back to current price)
      if (!entryPriceNum && screenerOutput.analysis_summary) {
        entryPriceNum = extractPrice(screenerOutput.analysis_summary, true);
      }

      // 5. Extract stop loss and take profit from top level if not already found
      if (stopLossNum == null) {
        stopLossNum = extractPrice(screenerOutput.stop_loss_level || screenerOutput.stop_loss, true);
      }
      if (takeProfitNum == null) {
        takeProfitNum = extractPrice(screenerOutput.take_profit_target || screenerOutput.take_profit, true);
      }
      
      // 6. ONLY use current price as LAST resort if no entry zone found in recommendation
      if (!entryPriceNum && data.asset_profile?.price_context?.candles?.length > 0) {
        const candles = data.asset_profile.price_context.candles;
        const latestCandle = candles[candles.length - 1];
        if (latestCandle?.close) {
          entryPriceNum = typeof latestCandle.close === 'number' 
            ? latestCandle.close 
            : parseFloat(String(latestCandle.close));
        }
      }

      if (!entryPriceNum || isNaN(entryPriceNum)) {
        setTrackingStatus({ 
          type: "position", 
          message: "Error: Could not extract entry price. Please ensure the recommendation includes an entry zone or price." 
        });
        setTimeout(() => setTrackingStatus({ type: null, message: "" }), 5000);
        return;
      }

      await trackPosition({
        positionId,
        recommendationId,
        ticker: data.ticker,
        entryPrice: entryPriceNum,
        stopLoss: stopLossNum,
        takeProfit: takeProfitNum,
      });
      setTrackingStatus({ type: "position", message: "✓ Position tracked successfully!" });
      setTimeout(() => setTrackingStatus({ type: null, message: "" }), 5000);
      // Dispatch event to refresh Positions tab
      window.dispatchEvent(new CustomEvent('refresh-positions'));
    } catch (error: any) {
      setTrackingStatus({ type: "position", message: `Error: ${error.message}` });
      setTimeout(() => setTrackingStatus({ type: null, message: "" }), 5000);
    }
  };

  const handlePrintValidation = () => {
    if (!validationPrintRef.current) {
      window.print();
      return;
    }

    const clone = validationPrintRef.current.cloneNode(true) as HTMLElement;

    const printWindow = window.open("", "", "width=900,height=650");

    if (printWindow) {
      printWindow.document.write(`
      <html>
        <head>
          <title>Validation Results</title>
          <style>
            body { font-family: sans-serif; margin: 32px; color: #000 !important; }
            h1,h2,h3,h4,h5,h6 { color: #000 !important; }
            .panel, .panel-inner, .card {
              border: none !important;
              box-shadow: none !important;
              background: transparent !important;
            }
            *::-webkit-scrollbar { display: none !important; }
          </style>
        </head>
        <body>${clone.innerHTML}</body>
      </html>
      `);

      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 200);
    }
  };

  return (
    <div className="analysis-grid">
      {/* Market Regime Badge */}
      {marketRegime && (
        <div className="card" style={{ marginBottom: "1rem", padding: "1rem", backgroundColor: "#1a1d29", border: "1px solid #2c3142" }}>
          <h3 style={{ color: "#ffffff", fontSize: "1rem", fontWeight: "bold", marginBottom: "0.75rem" }}>
            Market Regime Analysis
          </h3>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.5rem" }}>
            <span
              style={{
                display: "inline-block",
                padding: "0.5rem 1rem",
                borderRadius: "6px",
                backgroundColor: getRegimeBadgeColor(marketRegime.regime_type),
                color: "white",
                fontWeight: "bold",
                fontSize: "0.875rem"
              }}
            >
              {marketRegime.regime_type}
            </span>
            <span style={{ fontSize: "0.875rem", color: "#ffffff" }}>
              Confidence: {marketRegime.regime_confidence}
            </span>
          </div>
          <p style={{ fontSize: "0.875rem", color: "#ffffff", marginBottom: "0.5rem", lineHeight: "1.5" }}>
            {marketRegime.regime_description}
          </p>
          {marketRegime.strategy_adjustments && (
            <div style={{ fontSize: "0.8rem", color: "#ffffff", fontStyle: "italic", marginTop: "8px" }}>
              <span style={{ fontWeight: "600", marginRight: "8px" }}>Strategy Adjustment:</span>
              <span>{marketRegime.strategy_adjustments}</span>
            </div>
          )}
        </div>
      )}

      {/* Screener Analysis Results */}
      <div style={{ padding: "16px", backgroundColor: "#1a1d29", borderRadius: "12px", border: "2px solid #2c3142" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <h3 style={{ fontWeight: "700", fontSize: "1.125rem", color: "#ffffff", margin: 0 }}>Screener Analysis Results</h3>
          </div>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button
              className="primary-button"
              onClick={handleTrackRecommendation}
              style={{ fontSize: "0.875rem", padding: "0.5rem 1rem" }}
            >
              Track Trade
            </button>
            <button
              className="primary-button"
              onClick={handleTrackPosition}
              style={{ fontSize: "0.875rem", padding: "0.5rem 1rem" }}
            >
              Track Position
            </button>
          </div>
        </div>
        {trackingStatus.type && (
          <div style={{
            padding: "0.75rem 1rem",
            marginBottom: "1rem",
            borderRadius: "6px",
            backgroundColor: trackingStatus.message.includes("Error") ? "#fee2e2" : "#d1fae5",
            color: trackingStatus.message.includes("Error") ? "#991b1b" : "#065f46",
            fontSize: "0.95rem",
            fontWeight: "500",
            border: `2px solid ${trackingStatus.message.includes("Error") ? "#fca5a5" : "#86efac"}`,
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem"
          }}>
            <span style={{ fontSize: "1.2rem" }}>
              {trackingStatus.message.includes("Error") ? "⚠️" : "✓"}
            </span>
            <span>{trackingStatus.message}</span>
          </div>
        )}
        <div className="flex items-center gap-2 mb-3">
          {screenerOutput.interval_override_note && (
            <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full font-semibold" title={screenerOutput.interval_override_note}>
              Chart Interval: {data.chart_interval}
            </span>
          )}
          {validationOutput.correction_applied && (
            <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full font-semibold">
              ✓ Corrected by Validation Agent
            </span>
          )}
        </div>
        
        {/* Show interval override note if present */}
        {screenerOutput.interval_override_note && (
          <div style={{ marginBottom: "12px", padding: "8px", backgroundColor: "rgba(59, 130, 246, 0.1)", borderRadius: "8px", border: "1px solid #3b82f6", fontSize: "0.875rem", color: "#93c5fd" }}>
            <span style={{ fontWeight: "600", marginRight: "8px", color: "#ffffff" }}>Note:</span>
            <span style={{ color: "#9ca3af" }}>{screenerOutput.interval_override_note}</span>
          </div>
        )}
        
        {/* User Specified Screener: LLM Agent Response at Top */}
        {isUserSpecified && screenerOutput.methodology && (
          <div style={{ marginBottom: "16px", padding: "16px", backgroundColor: "#1e3a5f", borderRadius: "8px", border: "2px solid #3b82f6" }}>
            <h4 style={{ fontWeight: "700", fontSize: "1.125rem", marginBottom: "12px", color: "#ffffff" }}>LLM Agent Response</h4>
            <div style={{ fontSize: "0.95rem", color: "#e0e7ff", lineHeight: "1.7", whiteSpace: "pre-wrap" }}>
              {typeof screenerOutput.methodology === "string" 
                ? screenerOutput.methodology 
                : JSON.stringify(screenerOutput.methodology, null, 2)}
            </div>
          </div>
        )}
        
        {/* Trade Summary - Main Recommendation */}
        {screenerOutput.trade && (
          <div style={{ marginBottom: "16px", padding: "16px", backgroundColor: "#1a1d29", borderRadius: "8px", border: "2px solid #2c3142" }}>
            <h4 style={{ fontWeight: "700", fontSize: "1.25rem", marginBottom: "16px", color: "#ffffff" }}>Trade Recommendation</h4>
            
            {/* Validation Warning Banner - Show prominently if validation failed with price relationship errors */}
            {(() => {
              // DIRECT PRICE VALIDATION - Check actual trade prices regardless of validation output
              const trade = screenerOutput.trade;
              const directPriceErrors: string[] = [];
              
              console.log("=== DIRECT PRICE CHECK ===");
              console.log("Full screenerOutput:", JSON.stringify(screenerOutput, null, 2));
              console.log("Trade object:", trade);
              
              if (!trade) {
                console.log("No trade object found, not showing banner");
                return null;
              }
              
              if (trade.entry !== null && trade.entry !== undefined) {
                const entry = typeof trade.entry === 'number' ? trade.entry : parseFloat(String(trade.entry));
                const stop = trade.stop !== null && trade.stop !== undefined 
                  ? (typeof trade.stop === 'number' ? trade.stop : parseFloat(String(trade.stop)))
                  : null;
                const targets = Array.isArray(trade.targets) 
                  ? trade.targets.map((t: any) => typeof t === 'number' ? t : parseFloat(String(t))).filter((t: number) => !isNaN(t))
                  : [];
                const bias = trade.bias?.toLowerCase();
                
                console.log("Parsed values:", { entry, stop, targets, bias });
                console.log("Entry isNaN:", isNaN(entry), "Entry value:", entry);
                console.log("Targets array:", targets);
                console.log("Target values:", targets.map((t: number, i: number) => `Target ${i+1}: $${t.toFixed(2)}`));
                
                if (!isNaN(entry) && bias) {
                  if (bias === "bullish") {
                    // For bullish: stop < entry < targets
                    console.log("Checking bullish trade relationships...");
                    if (stop !== null && !isNaN(stop) && stop >= entry) {
                      const error = `Stop loss ($${stop.toFixed(2)}) is at or above entry ($${entry.toFixed(2)}) for bullish trade`;
                      console.log("ERROR:", error);
                      directPriceErrors.push(error);
                    }
                    targets.forEach((target: number, i: number) => {
                      if (!isNaN(target)) {
                        console.log(`Checking target ${i+1}: $${target.toFixed(2)} vs entry: $${entry.toFixed(2)}, stop: ${stop !== null ? `$${stop.toFixed(2)}` : 'null'}`);
                        if (target <= entry) {
                          const error = `Target ${i+1} ($${target.toFixed(2)}) is at or below entry ($${entry.toFixed(2)}) for bullish trade`;
                          console.log("ERROR:", error);
                          directPriceErrors.push(error);
                        }
                        if (stop !== null && !isNaN(stop) && target <= stop) {
                          const error = `Target ${i+1} ($${target.toFixed(2)}) is at or below stop ($${stop.toFixed(2)}) for bullish trade`;
                          console.log("ERROR:", error);
                          directPriceErrors.push(error);
                        }
                      }
                    });
                  } else if (bias === "bearish") {
                    // For bearish: targets < entry < stop
                    console.log("Checking bearish trade relationships...");
                    console.log(`Expected: targets < entry (${entry.toFixed(2)}) < stop (${stop !== null ? stop.toFixed(2) : 'null'})`);
                    if (stop !== null && !isNaN(stop) && stop <= entry) {
                      const error = `Stop loss ($${stop.toFixed(2)}) is at or below entry ($${entry.toFixed(2)}) for bearish trade`;
                      console.log("ERROR:", error);
                      directPriceErrors.push(error);
                    }
                    targets.forEach((target: number, i: number) => {
                      if (!isNaN(target)) {
                        console.log(`Checking target ${i+1}: $${target.toFixed(2)} vs entry: $${entry.toFixed(2)}, stop: ${stop !== null ? `$${stop.toFixed(2)}` : 'null'}`);
                        if (target >= entry) {
                          const error = `Target ${i+1} ($${target.toFixed(2)}) is at or above entry ($${entry.toFixed(2)}) for bearish trade`;
                          console.log("ERROR:", error);
                          directPriceErrors.push(error);
                        }
                        if (stop !== null && !isNaN(stop) && target >= stop) {
                          const error = `Target ${i+1} ($${target.toFixed(2)}) is at or above stop ($${stop.toFixed(2)}) for bearish trade`;
                          console.log("ERROR:", error);
                          directPriceErrors.push(error);
                        }
                        // Additional check: if target is way below entry, flag as potentially problematic
                        // For bearish trades, targets should be reasonable (not more than 15% below entry typically)
                        if (target < entry * 0.85) {
                          const error = `Target ${i+1} ($${target.toFixed(2)}) is more than 15% below entry ($${entry.toFixed(2)}) for bearish trade - this seems overly aggressive`;
                          console.log("ERROR:", error);
                          directPriceErrors.push(error);
                        }
                      }
                    });
                  }
                } else {
                  console.log("Skipping price check - entry isNaN:", isNaN(entry), "bias:", bias);
                }
              }
              
              console.log("Direct price errors found:", directPriceErrors);
              
              // Also check validation output for errors
              const validationErrors: string[] = [];
              if (validationOutput.issues) {
                const issuesArray = Array.isArray(validationOutput.issues) 
                  ? validationOutput.issues 
                  : [validationOutput.issues];
                issuesArray.forEach((issue: any) => {
                  const issueStr = String(issue);
                  if (issueStr.toLowerCase().includes("invalid price relationship") ||
                      issueStr.toLowerCase().includes("target") ||
                      issueStr.toLowerCase().includes("stop") ||
                      issueStr.toLowerCase().includes("illogical")) {
                    validationErrors.push(issueStr);
                  }
                });
              }
              
              const hasErrors = directPriceErrors.length > 0 || validationErrors.length > 0;
              
              console.log("=== FINAL CHECK ===");
              console.log("Direct price errors:", directPriceErrors.length);
              console.log("Validation errors:", validationErrors.length);
              console.log("Has errors:", hasErrors);
              
              if (!hasErrors) {
                console.log("No price relationship errors found, not showing banner");
                return null;
              }
              
              console.log("SHOWING BANNER - Direct errors:", directPriceErrors.length, "Validation errors:", validationErrors.length);
              
              return (
                <div style={{
                  marginBottom: "16px",
                  padding: "16px",
                  backgroundColor: "#7f1d1d",
                  borderRadius: "8px",
                  border: "3px solid #ef4444",
                  boxShadow: "0 4px 6px rgba(239, 68, 68, 0.3)"
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
                    <span style={{ fontSize: "1.5rem" }}>⚠️</span>
                    <h5 style={{ 
                      fontWeight: "700", 
                      fontSize: "1.125rem", 
                      color: "#ffffff",
                      margin: 0
                    }}>
                      TRADE REJECTED - Invalid Price Relationships
                    </h5>
                  </div>
                  <div style={{ 
                    fontSize: "0.875rem", 
                    color: "#fecaca", 
                    marginBottom: "12px",
                    lineHeight: "1.5"
                  }}>
                    This trade recommendation has been rejected due to invalid price relationships. 
                    The entry, stop loss, and target prices do not align logically with the trade bias.
                  </div>
                  <div style={{ 
                    fontSize: "0.875rem", 
                    color: "#ffffff",
                    fontWeight: "600",
                    marginBottom: "8px"
                  }}>
                    Validation Errors:
                  </div>
                  <ul style={{ 
                    marginLeft: "20px", 
                    marginTop: "4px", 
                    padding: 0,
                    color: "#fecaca"
                  }}>
                    {(() => {
                      // Combine direct price errors and validation errors
                      const allErrors = [...directPriceErrors];
                      if (validationOutput.issues) {
                        const issuesArray = Array.isArray(validationOutput.issues) 
                          ? validationOutput.issues 
                          : [validationOutput.issues];
                        issuesArray.forEach((issue: any) => {
                          const issueStr = String(issue).toLowerCase();
                          if (issueStr.includes("invalid price relationship") ||
                              issueStr.includes("target") ||
                              issueStr.includes("stop") ||
                              issueStr.includes("illogical")) {
                            allErrors.push(String(issue));
                          }
                        });
                      }
                      return allErrors.map((error: string, idx: number) => (
                        <li key={idx} style={{ fontSize: "0.875rem", marginBottom: "4px" }}>
                          {error}
                        </li>
                      ));
                    })()}
                  </ul>
                  <div style={{ 
                    marginTop: "12px",
                    padding: "8px",
                    backgroundColor: "rgba(239, 68, 68, 0.2)",
                    borderRadius: "6px",
                    fontSize: "0.875rem",
                    color: "#ffffff",
                    fontWeight: "600"
                  }}>
                    ⛔ DO NOT USE THIS TRADE - The price relationships are illogical and would result in guaranteed losses.
                  </div>
                </div>
              );
            })()}
            
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px", marginBottom: "16px" }}>
              {/* Bias */}
              {screenerOutput.trade.bias && (
                <div style={{ padding: "12px", backgroundColor: "#2c3142", borderRadius: "8px" }}>
                  <div style={{ fontSize: "0.75rem", color: "#9ca3af", marginBottom: "0.5rem" }}>Bias</div>
                  <div style={{
                    fontSize: "1.5rem",
                    fontWeight: "700",
                    color: screenerOutput.trade.bias === "bullish" ? "#22c55e" :
                           screenerOutput.trade.bias === "bearish" ? "#ef4444" : "#9ca3af"
                  }}>
                    {screenerOutput.trade.bias.toUpperCase()}
                  </div>
                </div>
              )}
              
              {/* Confidence */}
              {screenerOutput.trade.confidence !== undefined && (
                <div style={{ padding: "12px", backgroundColor: "#2c3142", borderRadius: "8px" }}>
                  <div style={{ fontSize: "0.75rem", color: "#9ca3af", marginBottom: "0.5rem" }}>Confidence</div>
                  <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "#ffffff" }}>
                    {(screenerOutput.trade.confidence * 100).toFixed(0)}%
                  </div>
                </div>
              )}
              
              {/* Entry */}
              {screenerOutput.trade.entry !== null && screenerOutput.trade.entry !== undefined && (
                <div style={{ padding: "12px", backgroundColor: "#2c3142", borderRadius: "8px" }}>
                  <div style={{ fontSize: "0.75rem", color: "#9ca3af", marginBottom: "0.5rem" }}>Entry Price</div>
                  <div style={{ fontSize: "1.25rem", fontWeight: "700", color: "#ffffff" }}>
                    ${typeof screenerOutput.trade.entry === 'number' ? screenerOutput.trade.entry.toFixed(2) : screenerOutput.trade.entry}
                  </div>
                  {screenerOutput.trade.entry_type && (
                    <div style={{ fontSize: "0.75rem", color: "#9ca3af", marginTop: "4px" }}>Type: {screenerOutput.trade.entry_type}</div>
                  )}
                </div>
              )}
              
              {/* Stop Loss */}
              {screenerOutput.trade.stop !== null && screenerOutput.trade.stop !== undefined && (
                <div style={{ padding: "12px", backgroundColor: "#2c3142", borderRadius: "8px" }}>
                  <div style={{ fontSize: "0.75rem", color: "#9ca3af", marginBottom: "0.5rem" }}>Stop Loss</div>
                  <div style={{ fontSize: "1.25rem", fontWeight: "700", color: "#ef4444" }}>
                    ${typeof screenerOutput.trade.stop === 'number' ? screenerOutput.trade.stop.toFixed(2) : screenerOutput.trade.stop}
                  </div>
                </div>
              )}
            </div>
            
            {/* Targets */}
            {screenerOutput.trade.targets && Array.isArray(screenerOutput.trade.targets) && screenerOutput.trade.targets.length > 0 && (
              <div style={{ marginBottom: "16px" }}>
                <div style={{ fontSize: "0.875rem", color: "#9ca3af", marginBottom: "8px" }}>Take Profit Targets</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                  {screenerOutput.trade.targets.map((target: number, idx: number) => (
                    <div key={idx} style={{ padding: "8px 12px", backgroundColor: "rgba(34, 197, 94, 0.1)", color: "#22c55e", borderRadius: "8px", fontWeight: "600", fontSize: "0.875rem", border: "1px solid rgba(34, 197, 94, 0.3)" }}>
                      Target {idx + 1}: ${typeof target === 'number' ? target.toFixed(2) : target}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Setup Description */}
            {screenerOutput.trade.setup && (
              <div style={{ marginBottom: "12px", padding: "12px", backgroundColor: "#2c3142", borderRadius: "8px" }}>
                <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#ffffff", marginBottom: "8px" }}>Trade Setup</div>
                <div style={{ fontSize: "0.875rem", color: "#9ca3af", whiteSpace: "pre-wrap" }}>
                  {screenerOutput.trade.setup}
                </div>
              </div>
            )}
            
            {/* Time Horizon */}
            {screenerOutput.trade.time_horizon && (
              <div style={{ marginBottom: "12px", display: "flex", alignItems: "baseline" }}>
                <span style={{ fontSize: "0.875rem", color: "#9ca3af", marginRight: "8px", minWidth: "120px" }}>Time Horizon:</span>
                <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#ffffff" }}>{screenerOutput.trade.time_horizon}</span>
              </div>
            )}
            
            {/* Invalidation */}
            {screenerOutput.trade.invalidation && (
              <div style={{ marginBottom: "12px", padding: "12px", backgroundColor: "rgba(245, 158, 11, 0.1)", borderRadius: "8px", border: "1px solid #f59e0b" }}>
                <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#f59e0b", marginBottom: "4px" }}>Invalidation Condition</div>
                <div style={{ fontSize: "0.875rem", color: "#ffffff", whiteSpace: "pre-wrap" }}>
                  {screenerOutput.trade.invalidation}
                </div>
              </div>
            )}
            
            {/* Risks */}
            {screenerOutput.trade.risks && Array.isArray(screenerOutput.trade.risks) && screenerOutput.trade.risks.length > 0 && (
              <div style={{ marginBottom: "12px", padding: "12px", backgroundColor: "rgba(239, 68, 68, 0.1)", borderRadius: "8px", border: "1px solid #ef4444" }}>
                <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#ef4444", marginBottom: "8px" }}>Risks</div>
                <ul style={{ listStyle: "disc", listStylePosition: "inside", fontSize: "0.875rem", color: "#ffffff", margin: 0, padding: 0 }}>
                  {screenerOutput.trade.risks.map((risk: string, idx: number) => (
                    <li key={idx} style={{ marginBottom: "4px" }}>{risk}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        
        {/* First Candle Rule Specific Fields */}
        {isFirstCandleRule && (
          <div style={{ marginBottom: "16px", padding: "12px", backgroundColor: "#1a1d29", borderRadius: "8px", border: "2px solid #2c3142" }}>
            <h4 style={{ fontWeight: "700", fontSize: "1rem", marginBottom: "12px", color: "#ffffff" }}>First Candle Rule Analysis</h4>
            
            {screenerOutput.first_candle_high !== undefined && (
              <div style={{ marginBottom: "8px", display: "flex", alignItems: "baseline" }}>
                <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#9ca3af", marginRight: "8px", minWidth: "140px" }}>First Candle High:</span>
                <span style={{ fontSize: "0.875rem", fontWeight: "700", color: "#ffffff" }}>${screenerOutput.first_candle_high?.toFixed(2) || "N/A"}</span>
              </div>
            )}
            
            {screenerOutput.first_candle_low !== undefined && (
              <div style={{ marginBottom: "8px", display: "flex", alignItems: "baseline" }}>
                <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#9ca3af", marginRight: "8px", minWidth: "140px" }}>First Candle Low:</span>
                <span style={{ fontSize: "0.875rem", fontWeight: "700", color: "#ffffff" }}>${screenerOutput.first_candle_low?.toFixed(2) || "N/A"}</span>
              </div>
            )}
            
            {screenerOutput.first_candle_open !== undefined && (
              <div style={{ marginBottom: "8px", display: "flex", alignItems: "baseline" }}>
                <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#9ca3af", marginRight: "8px", minWidth: "140px" }}>First Candle Open:</span>
                <span style={{ fontSize: "0.875rem", fontWeight: "700", color: "#ffffff" }}>${screenerOutput.first_candle_open?.toFixed(2) || "N/A"}</span>
              </div>
            )}
            
            {screenerOutput.first_candle_close !== undefined && (
              <div style={{ marginBottom: "8px", display: "flex", alignItems: "baseline" }}>
                <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#9ca3af", marginRight: "8px", minWidth: "140px" }}>First Candle Close:</span>
                <span style={{ fontSize: "0.875rem", fontWeight: "700", color: "#ffffff" }}>${screenerOutput.first_candle_close?.toFixed(2) || "N/A"}</span>
              </div>
            )}
            
            {screenerOutput.first_candle_timestamp && (
              <div style={{ marginBottom: "8px", display: "flex", alignItems: "baseline" }}>
                <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#9ca3af", marginRight: "8px", minWidth: "140px" }}>First Candle Timestamp:</span>
                <span style={{ fontSize: "0.875rem", fontWeight: "700", color: "#ffffff" }}>{screenerOutput.first_candle_timestamp}</span>
              </div>
            )}
            
            {screenerOutput.current_price !== undefined && (
              <div style={{ marginBottom: "8px", display: "flex", alignItems: "baseline" }}>
                <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#9ca3af", marginRight: "8px", minWidth: "140px" }}>Current Price:</span>
                <span style={{ fontSize: "0.875rem", fontWeight: "700", color: "#ffffff" }}>${screenerOutput.current_price?.toFixed(2) || "N/A"}</span>
              </div>
            )}
            
            {screenerOutput.breakout_status && (
              <div style={{ marginBottom: "8px", display: "flex", alignItems: "baseline" }}>
                <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#9ca3af", marginRight: "8px", minWidth: "140px" }}>Breakout Status:</span>
                <span style={{
                  color: screenerOutput.breakout_status === "Above High" ? "#22c55e" :
                         screenerOutput.breakout_status === "Below Low" ? "#ef4444" : "#9ca3af",
                  fontWeight: "bold",
                  fontSize: "0.875rem"
                }}>
                  {screenerOutput.breakout_status}
                </span>
              </div>
            )}
            
            {screenerOutput.entry_signal && (
              <div style={{ marginBottom: "8px", display: "flex", alignItems: "baseline" }}>
                <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#9ca3af", marginRight: "8px", minWidth: "140px" }}>Entry Signal:</span>
                <span style={{
                  color: screenerOutput.entry_signal === "LONG" ? "#22c55e" :
                         screenerOutput.entry_signal === "SHORT" ? "#ef4444" : "#9ca3af",
                  fontWeight: "bold",
                  fontSize: "0.875rem"
                }}>
                  {screenerOutput.entry_signal}
                </span>
              </div>
            )}
            
            {screenerOutput.entry_price_zone && (
              <div style={{ marginBottom: "8px", display: "flex", alignItems: "baseline" }}>
                <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#9ca3af", marginRight: "8px", minWidth: "140px" }}>Entry Price Zone:</span>
                <span style={{ fontSize: "0.875rem", fontWeight: "700", color: "#ffffff" }}>{screenerOutput.entry_price_zone}</span>
              </div>
            )}
            
            {screenerOutput.stop_loss_level !== undefined && (
              <div style={{ marginBottom: "8px", display: "flex", alignItems: "baseline" }}>
                <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#9ca3af", marginRight: "8px", minWidth: "140px" }}>Stop Loss Level:</span>
                <span style={{ fontSize: "0.875rem", fontWeight: "700", color: "#ef4444" }}>${screenerOutput.stop_loss_level?.toFixed(2) || "N/A"}</span>
              </div>
            )}
            
            {screenerOutput.take_profit_target && (
              <div style={{ marginBottom: "8px", display: "flex", alignItems: "baseline" }}>
                <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#9ca3af", marginRight: "8px", minWidth: "140px" }}>Take Profit Target:</span>
                <span style={{ fontSize: "0.875rem", fontWeight: "700", color: "#22c55e" }}>{screenerOutput.take_profit_target}</span>
              </div>
            )}
            
            {screenerOutput.risk_reward_ratio && (
              <div style={{ marginBottom: "8px", display: "flex", alignItems: "baseline" }}>
                <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#9ca3af", marginRight: "8px", minWidth: "140px" }}>Risk/Reward Ratio:</span>
                <span style={{ fontSize: "0.875rem", fontWeight: "700", color: "#ffffff" }}>{screenerOutput.risk_reward_ratio}</span>
              </div>
            )}
            
            {screenerOutput.strategy_confidence && (
              <div style={{ marginBottom: "8px", display: "flex", alignItems: "baseline" }}>
                <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#9ca3af", marginRight: "8px", minWidth: "140px" }}>Strategy Confidence:</span>
                <span style={{ fontSize: "0.875rem", fontWeight: "700", color: "#ffffff" }}>{screenerOutput.strategy_confidence}</span>
              </div>
            )}
            
            {screenerOutput.fair_value_gaps && Array.isArray(screenerOutput.fair_value_gaps) && screenerOutput.fair_value_gaps.length > 0 && (
              <div style={{ marginBottom: "8px" }}>
                <div style={{ marginBottom: "8px" }}>
                  <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#ffffff" }}>Fair Value Gaps:</span>
                </div>
                <ul style={{ marginLeft: "20px", marginTop: "4px" }}>
                  {screenerOutput.fair_value_gaps.map((fvg: any, idx: number) => (
                    <li key={idx} style={{ fontSize: "0.875rem", color: "#9ca3af" }}>
                      <span style={{ fontWeight: "600", color: "#ffffff" }}>{fvg.gap_type || "Gap"}:</span>{" "}
                      <span style={{ fontWeight: "700", color: "#ffffff" }}>${fvg.lower_level?.toFixed(2)} - ${fvg.upper_level?.toFixed(2)}</span>
                      {fvg.description && <span style={{ color: "#9ca3af" }}> ({fvg.description})</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {screenerOutput.additional_observations && (
              <div style={{ marginBottom: "8px" }}>
                <div style={{ marginBottom: "4px" }}>
                  <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#ffffff" }}>Additional Observations:</span>
                </div>
                <div style={{ fontSize: "0.875rem", color: "#9ca3af", whiteSpace: "pre-wrap" }}>
                  {typeof screenerOutput.additional_observations === "string"
                    ? screenerOutput.additional_observations
                    : JSON.stringify(screenerOutput.additional_observations, null, 2)}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* MACD Money Map Specific Display */}
        {isMacdMoneyMap && (
          <div className="mb-4">
            {/* Final Recommendations */}
            {screenerOutput.final_recommendations && Array.isArray(screenerOutput.final_recommendations) && screenerOutput.final_recommendations.length > 0 && (
              <div style={{ marginBottom: "16px", padding: "16px", backgroundColor: "#1a1d29", borderRadius: "8px", border: "2px solid #2c3142" }}>
                <h4 style={{ fontWeight: "700", fontSize: "1.125rem", marginBottom: "12px", color: "#ffffff" }}>Final Recommendations</h4>
                {screenerOutput.final_recommendations.map((rec: any, idx: number) => (
                  <div key={idx} style={{ marginBottom: "12px", padding: "12px", backgroundColor: "#2c3142", borderRadius: "8px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px", flexWrap: "wrap" }}>
                      <span style={{
                        padding: "4px 12px",
                        borderRadius: "999px",
                        fontWeight: "700",
                        fontSize: "0.875rem",
                        backgroundColor: rec.signal_type === "BUY" ? "rgba(34, 197, 94, 0.2)" : "rgba(239, 68, 68, 0.2)",
                        color: rec.signal_type === "BUY" ? "#22c55e" : "#ef4444",
                        border: `1px solid ${rec.signal_type === "BUY" ? "#22c55e" : "#ef4444"}`
                      }}>
                        {rec.signal_type}
                      </span>
                      <span style={{
                        padding: "4px 12px",
                        borderRadius: "999px",
                        fontWeight: "700",
                        fontSize: "0.875rem",
                        backgroundColor: rec.grade === "A+" ? "rgba(139, 92, 246, 0.2)" :
                                         rec.grade === "B" ? "rgba(59, 130, 246, 0.2)" :
                                         "rgba(245, 158, 11, 0.2)",
                        color: rec.grade === "A+" ? "#8b5cf6" :
                               rec.grade === "B" ? "#3b82f6" : "#f59e0b",
                        border: `1px solid ${rec.grade === "A+" ? "#8b5cf6" :
                                         rec.grade === "B" ? "#3b82f6" : "#f59e0b"}`
                      }}>
                        Grade: {rec.grade}
                      </span>
                      <span style={{
                        padding: "4px 12px",
                        borderRadius: "999px",
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        backgroundColor: "rgba(156, 163, 175, 0.2)",
                        color: "#9ca3af",
                        border: "1px solid #6b7280"
                      }}>
                        {rec.confidence} Confidence
                      </span>
                    </div>
                    {rec.entry_price_zone && (
                      <div style={{ marginBottom: "4px", display: "flex", alignItems: "baseline" }}>
                        <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#9ca3af", marginRight: "8px", minWidth: "100px" }}>Entry Zone:</span>
                        <span style={{ fontSize: "0.875rem", fontWeight: "700", color: "#ffffff" }}>{rec.entry_price_zone}</span>
                      </div>
                    )}
                    {rec.stop_loss && (
                      <div style={{ marginBottom: "4px", display: "flex", alignItems: "baseline" }}>
                        <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#9ca3af", marginRight: "8px", minWidth: "100px" }}>Stop Loss:</span>
                        <span style={{ fontSize: "0.875rem", fontWeight: "700", color: "#ef4444" }}>${typeof rec.stop_loss === 'number' ? rec.stop_loss.toFixed(2) : rec.stop_loss}</span>
                      </div>
                    )}
                    {rec.take_profit && (
                      <div style={{ marginBottom: "4px", display: "flex", alignItems: "baseline" }}>
                        <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#9ca3af", marginRight: "8px", minWidth: "100px" }}>Take Profit:</span>
                        <span style={{ fontSize: "0.875rem", fontWeight: "700", color: "#22c55e" }}>${typeof rec.take_profit === 'number' ? rec.take_profit.toFixed(2) : rec.take_profit}</span>
                      </div>
                    )}
                    {rec.combined_reasoning && (
                      <div style={{ marginTop: "8px", paddingTop: "8px", borderTop: "1px solid #2c3142", fontSize: "0.875rem", color: "#9ca3af", whiteSpace: "pre-wrap" }}>
                        {rec.combined_reasoning}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            
            {/* System 1: Trend System */}
            {screenerOutput.trend_system && (
              <div style={{ marginBottom: "16px", padding: "12px", backgroundColor: "#1a1d29", borderRadius: "8px", border: "2px solid #2c3142" }}>
                <h4 style={{ fontWeight: "700", fontSize: "1rem", marginBottom: "12px", color: "#3b82f6" }}>System 1: Trend System</h4>
                {screenerOutput.trend_system.trend_bias && (
                  <div style={{ marginBottom: "8px", display: "flex", alignItems: "baseline" }}>
                    <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#9ca3af", marginRight: "8px", minWidth: "120px" }}>Trend Bias:</span>
                    <span style={{ fontSize: "0.875rem", fontWeight: "700", color: "#ffffff" }}>
                      {screenerOutput.trend_system.trend_bias}
                      {screenerOutput.trend_system.bias_percentage !== undefined && (
                        <span style={{ color: "#9ca3af" }}> ({screenerOutput.trend_system.bias_percentage.toFixed(1)}%)</span>
                      )}
                    </span>
                  </div>
                )}
                {screenerOutput.trend_system.valid_patience_signals && Array.isArray(screenerOutput.trend_system.valid_patience_signals) && (
                  <div style={{ marginBottom: "8px", display: "flex", alignItems: "baseline" }}>
                    <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#9ca3af", marginRight: "8px", minWidth: "120px" }}>Valid Trend Signals:</span>
                    <span style={{ fontSize: "0.875rem", fontWeight: "700", color: "#ffffff" }}>{screenerOutput.trend_system.valid_patience_signals.length}</span>
                  </div>
                )}
                {screenerOutput.trend_system.analysis_summary && (
                  <div style={{ marginTop: "8px", fontSize: "0.875rem", color: "#9ca3af", whiteSpace: "pre-wrap" }}>
                    {screenerOutput.trend_system.analysis_summary}
                  </div>
                )}
              </div>
            )}
            
            {/* System 2: Reversal System */}
            {screenerOutput.reversal_system && (
              <div style={{ marginBottom: "16px", padding: "12px", backgroundColor: "#1a1d29", borderRadius: "8px", border: "2px solid #2c3142" }}>
                <h4 style={{ fontWeight: "700", fontSize: "1rem", marginBottom: "12px", color: "#8b5cf6" }}>System 2: Reversal System</h4>
                {screenerOutput.reversal_system.detected_divergences && Array.isArray(screenerOutput.reversal_system.detected_divergences) && (
                  <div style={{ marginBottom: "8px", display: "flex", alignItems: "baseline" }}>
                    <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#9ca3af", marginRight: "8px", minWidth: "150px" }}>Divergences Detected:</span>
                    <span style={{ fontSize: "0.875rem", fontWeight: "700", color: "#ffffff" }}>{screenerOutput.reversal_system.detected_divergences.length}</span>
                  </div>
                )}
                {screenerOutput.reversal_system.validated_reversal_signals && Array.isArray(screenerOutput.reversal_system.validated_reversal_signals) && (
                  <div style={{ marginBottom: "8px", display: "flex", alignItems: "baseline" }}>
                    <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#9ca3af", marginRight: "8px", minWidth: "150px" }}>Validated Reversal Signals:</span>
                    <span style={{ fontSize: "0.875rem", fontWeight: "700", color: "#ffffff" }}>{screenerOutput.reversal_system.validated_reversal_signals.length}</span>
                  </div>
                )}
                {screenerOutput.reversal_system.analysis_summary && (
                  <div style={{ marginTop: "8px", fontSize: "0.875rem", color: "#9ca3af", whiteSpace: "pre-wrap" }}>
                    {screenerOutput.reversal_system.analysis_summary}
                  </div>
                )}
              </div>
            )}
            
            {/* System 3: Confirmation System */}
            {screenerOutput.confirmation_system && (
              <div style={{ marginBottom: "16px", padding: "12px", backgroundColor: "#1a1d29", borderRadius: "8px", border: "2px solid #2c3142" }}>
                <h4 style={{ fontWeight: "700", fontSize: "1rem", marginBottom: "12px", color: "#22c55e" }}>System 3: Confirmation System</h4>
                {screenerOutput.confirmation_system.signal_evaluations && Array.isArray(screenerOutput.confirmation_system.signal_evaluations) && (
                  <div style={{ marginBottom: "8px", display: "flex", alignItems: "baseline" }}>
                    <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#9ca3af", marginRight: "8px", minWidth: "140px" }}>Signals Evaluated:</span>
                    <span style={{ fontSize: "0.875rem", fontWeight: "700", color: "#ffffff" }}>{screenerOutput.confirmation_system.signal_evaluations.length}</span>
                  </div>
                )}
                {screenerOutput.confirmation_system.analysis_summary && (
                  <div style={{ marginTop: "8px", fontSize: "0.875rem", color: "#9ca3af", whiteSpace: "pre-wrap" }}>
                    {screenerOutput.confirmation_system.analysis_summary}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        
        {screenerOutput.analysis_summary && (
          <div style={{ marginBottom: "12px", padding: "12px", backgroundColor: "#2c3142", borderRadius: "8px" }}>
            <div style={{ marginBottom: "8px" }}>
              <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#ffffff" }}>Analysis Summary:</span>
            </div>
            <div style={{ fontSize: "0.875rem", color: "#9ca3af", whiteSpace: "pre-wrap" }}>
              {typeof screenerOutput.analysis_summary === "string" 
                ? screenerOutput.analysis_summary 
                : JSON.stringify(screenerOutput.analysis_summary, null, 2)}
            </div>
          </div>
        )}

        {screenerOutput.key_findings && (
          <div style={{ marginBottom: "12px", padding: "12px", backgroundColor: "#2c3142", borderRadius: "8px" }}>
            <div style={{ marginBottom: "8px" }}>
              <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#ffffff" }}>Key Findings:</span>
            </div>
            <div style={{ fontSize: "0.875rem", color: "#9ca3af", whiteSpace: "pre-wrap" }}>
              {typeof screenerOutput.key_findings === "string"
                ? screenerOutput.key_findings
                : JSON.stringify(screenerOutput.key_findings, null, 2)}
            </div>
          </div>
        )}

        {screenerOutput.signals_detected && (
          <div style={{ marginBottom: "12px", padding: "12px", backgroundColor: "#2c3142", borderRadius: "8px" }}>
            <div style={{ marginBottom: "8px" }}>
              <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#ffffff" }}>Signals Detected:</span>
            </div>
            <div style={{ fontSize: "0.875rem", color: "#9ca3af", whiteSpace: "pre-wrap" }}>
              {typeof screenerOutput.signals_detected === "string"
                ? screenerOutput.signals_detected
                : JSON.stringify(screenerOutput.signals_detected, null, 2)}
            </div>
          </div>
        )}

        {screenerOutput.entry_recommendation && (
          <div style={{ marginBottom: "12px" }}>
            <div style={{ marginBottom: "4px", display: "flex", alignItems: "baseline" }}>
              <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#9ca3af", marginRight: "8px", minWidth: "150px" }}>Entry Recommendation:</span>
              <span style={{ fontSize: "0.875rem", color: "#ffffff" }}>{screenerOutput.entry_recommendation}</span>
            </div>
          </div>
        )}

        {screenerOutput.exit_recommendation && (
          <div style={{ marginBottom: "12px" }}>
            <div style={{ marginBottom: "4px", display: "flex", alignItems: "baseline" }}>
              <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#9ca3af", marginRight: "8px", minWidth: "150px" }}>Exit Recommendation:</span>
              <span style={{ fontSize: "0.875rem", color: "#ffffff" }}>{screenerOutput.exit_recommendation}</span>
            </div>
          </div>
        )}

        {screenerOutput.risk_assessment && (
          <div style={{ marginBottom: "12px", padding: "12px", backgroundColor: "#2c3142", borderRadius: "8px" }}>
            <div style={{ marginBottom: "8px" }}>
              <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#ffffff" }}>Risk Assessment:</span>
            </div>
            <div style={{ fontSize: "0.875rem", color: "#9ca3af", whiteSpace: "pre-wrap" }}>
              {typeof screenerOutput.risk_assessment === "string"
                ? screenerOutput.risk_assessment
                : JSON.stringify(screenerOutput.risk_assessment, null, 2)}
            </div>
          </div>
        )}

        {screenerOutput.confidence_level && (
          <div style={{ marginBottom: "12px" }}>
            <div style={{ marginBottom: "4px", display: "flex", alignItems: "baseline" }}>
              <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#9ca3af", marginRight: "8px", minWidth: "150px" }}>Confidence Level:</span>
              <span style={{ fontSize: "0.875rem", fontWeight: "700", color: "#ffffff" }}>{screenerOutput.confidence_level}</span>
            </div>
          </div>
        )}

        {/* Display any other fields from screener output */}
        {Object.keys(screenerOutput).length > 0 && (
          <div className="mt-4" style={{ borderTop: "1px solid #e5e7eb", paddingTop: "16px" }}>
            <div 
              style={{ 
                marginBottom: "8px",
                cursor: "pointer",
                userSelect: "none",
                padding: "8px",
                backgroundColor: "#f9fafb",
                borderRadius: "4px",
                border: "1px solid #e5e7eb"
              }}
              onClick={() => setShowFullScreenerOutput(!showFullScreenerOutput)}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f3f4f6"}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#f9fafb"}
            >
              <strong style={{ 
                color: "#111827", 
                fontWeight: "bold", 
                display: "block",
                fontSize: "1rem"
              }}>
                Full Screener Output {showFullScreenerOutput ? "▼" : "▶"}
              </strong>
            </div>
            {showFullScreenerOutput && (
              <pre style={{ 
                marginTop: "8px", 
                padding: "10px", 
                background: "#f5f5f5", 
                borderRadius: "4px",
                overflow: "auto",
                fontSize: "0.85rem",
                color: "#000",
                border: "1px solid #ddd",
                maxHeight: "800px"
              }}>
                {(() => {
                  try {
                    // Handle circular references and non-serializable values
                    const seen = new WeakSet();
                    const replacer = (_key: string, value: any) => {
                      if (typeof value === "object" && value !== null) {
                        if (seen.has(value)) {
                          return "[Circular Reference]";
                        }
                        seen.add(value);
                      }
                      // Handle functions, undefined, etc.
                      if (typeof value === "function") {
                        return "[Function]";
                      }
                      if (value === undefined) {
                        return "[Undefined]";
                      }
                      return value;
                    };
                    return JSON.stringify(screenerOutput, replacer, 2);
                  } catch (e) {
                    return `Error serializing screener output: ${String(e)}\n\nRaw output: ${String(screenerOutput)}`;
                  }
                })()}
              </pre>
            )}
          </div>
        )}
      </div>

      {/* Validation Results */}
      <div className="card full-width" ref={validationPrintRef}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-lg">Validation Results</h3>
          <button className="primary-button" onClick={handlePrintValidation}>
            Print Validation
          </button>
        </div>
        
        {validationOutput.verified !== undefined && (
          <div style={{ marginBottom: "12px", display: "flex", alignItems: "baseline" }}>
            <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#9ca3af", marginRight: "8px", minWidth: "100px" }}>Verified:</span>
            <span style={{ 
              color: validationOutput.verified ? "#22c55e" : "#ef4444",
              fontWeight: "bold",
              fontSize: "0.875rem"
            }}>
              {validationOutput.verified ? "✓ Yes" : "✗ No"}
            </span>
          </div>
        )}

        {/* Correction Status */}
        {validationOutput.correction_applied !== undefined && (
          <div style={{ marginBottom: "12px", padding: "12px", borderRadius: "8px", backgroundColor: validationOutput.correction_applied ? "#dcfce7" : "#fef3c7", border: `1px solid ${validationOutput.correction_applied ? "#86efac" : "#fcd34d"}` }}>
            <div style={{ display: "flex", alignItems: "baseline", marginBottom: validationOutput.correction_applied ? "8px" : "0" }}>
              <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#374151", marginRight: "8px", minWidth: "140px" }}>Correction Status:</span>
              <span style={{ 
                color: validationOutput.correction_applied ? "#166534" : "#92400e",
                fontWeight: "bold",
                fontSize: "0.875rem"
              }}>
                {validationOutput.correction_applied ? "✓ Corrections Applied" : "⚠ Corrections Not Applied"}
              </span>
            </div>
            {validationOutput.correction_applied && (
              <div style={{ marginTop: "8px", fontSize: "0.875rem", color: "#166534" }}>
                The validation agent identified errors and provided corrected analysis. The corrected analysis is displayed above.
              </div>
            )}
          </div>
        )}

        {/* Questions for Analyst */}
        {validationOutput.questions_for_analyst && Array.isArray(validationOutput.questions_for_analyst) && validationOutput.questions_for_analyst.length > 0 && (
          <div style={{ marginBottom: "12px", padding: "12px", backgroundColor: "#fefce8", borderRadius: "8px", border: "1px solid #fde047" }}>
            <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#78350f", marginBottom: "8px" }}>Questions for Analyst:</div>
            <ul style={{ marginLeft: "20px", marginTop: "8px", padding: 0 }}>
              {validationOutput.questions_for_analyst.map((question: string, idx: number) => (
                <li key={idx} style={{ color: "#92400e", marginBottom: "4px", fontSize: "0.875rem" }}>{question}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Detailed Failure Reasons */}
        {validationOutput.detailed_failure_reasons && (
          <div style={{ marginBottom: "12px", padding: "12px", backgroundColor: "#fef2f2", borderRadius: "8px", border: "1px solid #fecaca" }}>
            <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#991b1b", marginBottom: "8px" }}>Detailed Failure Reasons:</div>
            <div style={{ color: "#991b1b", whiteSpace: "pre-wrap", fontSize: "0.875rem" }}>
              {typeof validationOutput.detailed_failure_reasons === "string"
                ? validationOutput.detailed_failure_reasons
                : JSON.stringify(validationOutput.detailed_failure_reasons, null, 2)}
            </div>
          </div>
        )}

        {validationOutput.completeness_score !== undefined && (
          <div style={{ marginBottom: "12px", display: "flex", alignItems: "baseline" }}>
            <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#9ca3af", marginRight: "8px", minWidth: "150px" }}>Completeness Score:</span>
            <span style={{ fontSize: "0.875rem", fontWeight: "700", color: "#111827" }}>{validationOutput.completeness_score}/100</span>
          </div>
        )}

        {validationOutput.data_support_score !== undefined && (
          <div style={{ marginBottom: "12px", display: "flex", alignItems: "baseline" }}>
            <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#9ca3af", marginRight: "8px", minWidth: "150px" }}>Data Support Score:</span>
            <span style={{ fontSize: "0.875rem", fontWeight: "700", color: "#111827" }}>{validationOutput.data_support_score}/100</span>
          </div>
        )}

        {validationOutput.recommendation_quality !== undefined && (
          <div style={{ marginBottom: "12px", display: "flex", alignItems: "baseline" }}>
            <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#9ca3af", marginRight: "8px", minWidth: "150px" }}>Recommendation Quality:</span>
            <span style={{ fontSize: "0.875rem", fontWeight: "700", color: "#111827" }}>{validationOutput.recommendation_quality}/100</span>
          </div>
        )}

        {validationOutput.overall_quality && (
          <div style={{ marginBottom: "12px" }}>
            <div style={{ marginBottom: "4px" }}>
              <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#374151" }}>Overall Quality:</span>
            </div>
            <div style={{ marginTop: "4px", whiteSpace: "pre-wrap", fontSize: "0.875rem", color: "#111827" }}>
              {typeof validationOutput.overall_quality === "string"
                ? validationOutput.overall_quality
                : JSON.stringify(validationOutput.overall_quality, null, 2)}
            </div>
          </div>
        )}

        {validationOutput.issues && (
          <div style={{ marginBottom: "12px" }}>
            <div style={{ marginBottom: "4px" }}>
              <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#374151" }}>Issues Found:</span>
            </div>
            <ul style={{ marginLeft: "20px", marginTop: "4px", padding: 0 }}>
              {Array.isArray(validationOutput.issues) ? (
                validationOutput.issues.map((issue: string, idx: number) => (
                  <li key={idx} style={{ fontSize: "0.875rem", color: "#111827", marginBottom: "4px" }}>{issue}</li>
                ))
              ) : (
                <li style={{ fontSize: "0.875rem", color: "#111827" }}>{String(validationOutput.issues)}</li>
              )}
            </ul>
          </div>
        )}

        {/* First Candle Rule Validation Details */}
        {isFirstCandleRule && validationOutput.first_candle_rule_implementation && (
          <div style={{ marginBottom: "12px", padding: "12px", backgroundColor: "#eff6ff", borderRadius: "8px", border: "1px solid #93c5fd" }}>
            <h4 style={{ fontWeight: "700", fontSize: "1rem", marginBottom: "8px", color: "#1e40af" }}>First Candle Rule Implementation Check</h4>
            <div style={{ marginBottom: "4px", display: "flex", alignItems: "baseline" }}>
              <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#9ca3af", marginRight: "8px", minWidth: "180px" }}>First Candle Identified:</span>
              <span style={{ color: validationOutput.first_candle_rule_implementation.first_candle_identified ? "#22c55e" : "#ef4444", fontWeight: "bold", fontSize: "0.875rem" }}>
                {validationOutput.first_candle_rule_implementation.first_candle_identified ? "✓ Yes" : "✗ No"}
              </span>
            </div>
            <div style={{ marginBottom: "4px", display: "flex", alignItems: "baseline" }}>
              <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#9ca3af", marginRight: "8px", minWidth: "180px" }}>Levels Extracted Correctly:</span>
              <span style={{ color: validationOutput.first_candle_rule_implementation.levels_extracted_correctly ? "#22c55e" : "#ef4444", fontWeight: "bold", fontSize: "0.875rem" }}>
                {validationOutput.first_candle_rule_implementation.levels_extracted_correctly ? "✓ Yes" : "✗ No"}
              </span>
            </div>
            <div style={{ marginBottom: "4px", display: "flex", alignItems: "baseline" }}>
              <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#9ca3af", marginRight: "8px", minWidth: "180px" }}>Breakout Analysis Correct:</span>
              <span style={{ color: validationOutput.first_candle_rule_implementation.breakout_analysis_correct ? "#22c55e" : "#ef4444", fontWeight: "bold", fontSize: "0.875rem" }}>
                {validationOutput.first_candle_rule_implementation.breakout_analysis_correct ? "✓ Yes" : "✗ No"}
              </span>
            </div>
            <div style={{ marginBottom: "4px", display: "flex", alignItems: "baseline" }}>
              <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#9ca3af", marginRight: "8px", minWidth: "180px" }}>FVG Analysis Complete:</span>
              <span style={{ color: validationOutput.first_candle_rule_implementation.fvg_analysis_complete ? "#22c55e" : "#ef4444", fontWeight: "bold", fontSize: "0.875rem" }}>
                {validationOutput.first_candle_rule_implementation.fvg_analysis_complete ? "✓ Yes" : "✗ No"}
              </span>
            </div>
            <div style={{ marginBottom: "4px", display: "flex", alignItems: "baseline" }}>
              <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#9ca3af", marginRight: "8px", minWidth: "180px" }}>Entry/Exit Levels Specific:</span>
              <span style={{ color: validationOutput.first_candle_rule_implementation.entry_exit_levels_specific ? "#22c55e" : "#ef4444", fontWeight: "bold", fontSize: "0.875rem" }}>
                {validationOutput.first_candle_rule_implementation.entry_exit_levels_specific ? "✓ Yes" : "✗ No"}
              </span>
            </div>
            <div style={{ marginBottom: "4px", display: "flex", alignItems: "baseline" }}>
              <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#9ca3af", marginRight: "8px", minWidth: "180px" }}>Uses Live Data Only:</span>
              <span style={{ color: validationOutput.first_candle_rule_implementation.uses_live_data_only ? "#22c55e" : "#ef4444", fontWeight: "bold", fontSize: "0.875rem" }}>
                {validationOutput.first_candle_rule_implementation.uses_live_data_only ? "✓ Yes" : "✗ No"}
              </span>
            </div>
          </div>
        )}

        {/* Display any other validation fields */}
        {Object.keys(validationOutput).length > 0 && (
          <div className="mt-4" style={{ borderTop: "1px solid #e5e7eb", paddingTop: "16px" }}>
            <div 
              style={{ 
                marginBottom: "8px",
                cursor: "pointer",
                userSelect: "none",
                padding: "8px",
                backgroundColor: "#f9fafb",
                borderRadius: "4px",
                border: "1px solid #e5e7eb"
              }}
              onClick={() => setShowFullValidationOutput(!showFullValidationOutput)}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f3f4f6"}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#f9fafb"}
            >
              <strong style={{ 
                color: "#111827", 
                fontWeight: "bold", 
                display: "block",
                fontSize: "1rem"
              }}>
                Full Validation Output {showFullValidationOutput ? "▼" : "▶"}
              </strong>
            </div>
            {showFullValidationOutput && (
              <pre style={{ 
                marginTop: "8px", 
                padding: "10px", 
                background: "#f5f5f5", 
                borderRadius: "4px",
                overflow: "auto",
                fontSize: "0.85rem",
                color: "#000",
                border: "1px solid #ddd",
                maxHeight: "800px"
              }}>
                {(() => {
                  try {
                    // Handle circular references and non-serializable values
                    const seen = new WeakSet();
                    const replacer = (_key: string, value: any) => {
                      if (typeof value === "object" && value !== null) {
                        if (seen.has(value)) {
                          return "[Circular Reference]";
                        }
                        seen.add(value);
                      }
                      // Handle functions, undefined, etc.
                      if (typeof value === "function") {
                        return "[Function]";
                      }
                      if (value === undefined) {
                        return "[Undefined]";
                      }
                      return value;
                    };
                    return JSON.stringify(validationOutput, replacer, 2);
                  } catch (e) {
                    return `Error serializing validation output: ${String(e)}\n\nRaw output: ${String(validationOutput)}`;
                  }
                })()}
              </pre>
            )}
          </div>
        )}
        
        {/* Show message if validation output is empty */}
        {Object.keys(validationOutput).length === 0 && (
          <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <p style={{ color: "#666", fontStyle: "italic" }}>
              No validation output available.
            </p>
          </div>
        )}
      </div>

      {/* Footer with metadata */}
      <div className="card-footer-row" style={{ marginTop: "1rem", paddingTop: "1rem", borderTop: "1px solid rgba(148, 163, 184, 0.2)" }}>
        <span className="meta-muted" style={{ fontSize: "0.75rem", color: "#9ca3af" }}>
          {data.data_provider ? `Data: ${data.data_provider}` : ''}
        </span>
      </div>
    </div>
  );
};

export default ScreenerResult;

